import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api, openAuthenticatedDownload } from '../lib/api';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { UploadSimple, DownloadSimple, FileXls, CheckCircle, Warning, TrendUp, TrendDown, Clock, ClipboardText } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function HpclMatcherPage() {
  const { user } = useAuth();
  const isOwner = user?.role === 'admin' || user?.role === 'owner';
  const isAssistant = user?.role === 'assistant';
  const isAccountant = user?.role === 'accountant';
  const isStaff = user?.role === 'staff' || user?.role === 'sub_staff';
  const isDistributor = user?.role === 'distributor';

  // distributor must have HPCL in oil_companies
  const distHasHpcl = !!(user?.oil_companies || []).map(o => String(o).toUpperCase()).includes('HPCL');
  const canAccess = isOwner || isAssistant || isAccountant || isStaff || (isDistributor && distHasHpcl);

  const [agencyFile, setAgencyFile] = useState(null);
  const [successFile, setSuccessFile] = useState(null);
  const [distributorFile, setDistributorFile] = useState(null);
  const [mode, setMode] = useState('3file'); // '2file' or '3file'
  const [processing, setProcessing] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [reports, setReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);

  const fetchReports = useCallback(async () => {
    if (!canAccess) return;
    setLoadingReports(true);
    try {
      const { data } = await api.get('/hpcl-matcher/reports');
      setReports(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load reports');
    } finally {
      setLoadingReports(false);
    }
  }, [canAccess]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const handleProcess = async (e) => {
    e.preventDefault();
    if (!agencyFile || !successFile) {
      toast.error('Agency file aur Success file dono chahiye');
      return;
    }
    if (mode === '3file' && !distributorFile) {
      toast.error('3-File Mode me Distributor File bhi chahiye (Missing sheet ke liye)');
      return;
    }
    setProcessing(true);
    setLastResult(null);
    try {
      const fd = new FormData();
      fd.append('agency_file', agencyFile);
      fd.append('success_file', successFile);
      if (mode === '3file' && distributorFile) fd.append('distributor_file', distributorFile);
      const { data } = await api.post('/hpcl-matcher/process', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setLastResult(data);
      toast.success('Report generated!');
      setAgencyFile(null);
      setSuccessFile(null);
      setDistributorFile(null);
      document.querySelectorAll('input[type=file]').forEach(i => { i.value = ''; });
      fetchReports();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Process fail hua');
    } finally {
      setProcessing(false);
    }
  };

  const downloadReport = (id, filename) => {
    openAuthenticatedDownload(`/api/hpcl-matcher/reports/${id}/download`, filename)
      .catch(e => toast.error(e?.response?.data?.detail || 'Download fail hua'));
  };

  if (!canAccess) {
    return <div className="p-6 text-center text-red-600 font-medium" data-testid="hpcl-access-denied">
      Access denied. Ye feature HPCL ke Admin/CA/Staff/Assistant aur HPCL distributors ke liye hai.
    </div>;
  }

  const fmtDT = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '-';

  return (
    <div className="space-y-6" data-testid="hpcl-matcher-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">HPCL Data Matcher</h1>
        <p className="text-sm text-gray-500 mt-1">Agency + Success + Distributor files match karo — color-coded Excel report generate hoga</p>
      </div>

      {/* Upload Form */}
      <form onSubmit={handleProcess} className="bg-white border border-gray-200 rounded-lg p-4 sm:p-5 space-y-4" data-testid="hpcl-upload-section">
        {/* Mode Selector */}
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm font-medium text-gray-700">Select Mode:</span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setMode('2file')}
              data-testid="mode-2file-btn"
              className={`px-4 py-2 rounded-lg text-sm font-semibold border transition ${
                mode === '2file'
                  ? 'bg-indigo-600 text-white border-indigo-600 shadow'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400'
              }`}
            >
              📋 2-File Mode
            </button>
            <button
              type="button"
              onClick={() => setMode('3file')}
              data-testid="mode-3file-btn"
              className={`px-4 py-2 rounded-lg text-sm font-semibold border transition ${
                mode === '3file'
                  ? 'bg-indigo-600 text-white border-indigo-600 shadow'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400'
              }`}
            >
              🔍 3-File Mode (Full)
            </button>
          </div>
          <span className="text-xs text-gray-500 ml-auto hidden md:inline">
            {mode === '2file'
              ? 'Agency + Success — category-wise breakdown, no Missing sheet'
              : 'Agency + Success + Distributor — includes Missing-in-System sheet with reasons'}
          </span>
        </div>

        <div className={`grid grid-cols-1 gap-4 ${mode === '3file' ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
          <FileSlot
            label="Agency File"
            required
            file={agencyFile}
            onChange={setAgencyFile}
            hint="Column A = Consumer Number | Column B = Mobile Number"
            testId="agency-file-input"
          />
          <FileSlot
            label="Success File (HPCL)"
            required
            file={successFile}
            onChange={setSuccessFile}
            hint="Mobile | Status | Transaction | ... | Refunded (E)"
            testId="success-file-input"
          />
          {mode === '3file' && (
            <FileSlot
              label="Distributor File"
              required
              file={distributorFile}
              onChange={setDistributorFile}
              hint="Consumer Numbers received in system"
              testId="distributor-file-input"
            />
          )}
        </div>

        <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2.5">
          <Warning size={16} className="flex-shrink-0 mt-0.5" />
          <span>
            File format: .xlsx/.xls.{' '}
            {mode === '2file'
              ? 'Output me Success, Pending, Refunded sheets aayenge — Missing sheet NAHI banegi (Distributor file chahiye uske liye).'
              : 'Output me Success/Pending/Refunded + separate Missing sheet banegi jisme har row ka reason hoga.'}
          </span>
        </div>

        <Button type="submit" disabled={processing || !agencyFile || !successFile || (mode === '3file' && !distributorFile)}
          className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700" data-testid="hpcl-process-btn">
          <UploadSimple size={16} className="mr-2" />
          {processing ? 'Processing...' : `Generate Color-Coded Excel Report`}
        </Button>
      </form>

      {/* Last Result Summary */}
      {lastResult && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3" data-testid="hpcl-last-result">
          <div className="flex items-center gap-2 text-green-700">
            <CheckCircle size={20} weight="fill" />
            <span className="font-semibold">Report Ready</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <StatCard label="Total" value={lastResult.stats.total} color="gray" />
            <StatCard label="Success" value={lastResult.stats.success} color="green" />
            <StatCard label="Pending" value={lastResult.stats.pending} color="yellow" />
            <StatCard label="Refunded" value={lastResult.stats.refunded} color="red" />
            {lastResult.stats.missing !== null && (
              <StatCard label="Missing" value={lastResult.stats.missing} color="orange" />
            )}
          </div>
          <Button onClick={() => downloadReport(lastResult.id, lastResult.filename)}
            className="bg-green-600 hover:bg-green-700" data-testid="download-last-report-btn">
            <DownloadSimple size={16} className="mr-2" /> Download {lastResult.filename}
          </Button>
        </div>
      )}

      {/* History */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-5" data-testid="hpcl-history">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Past Reports</h2>

        {loadingReports ? (
          <p className="text-sm text-gray-500">Loading...</p>
        ) : reports.length === 0 ? (
          <p className="text-sm text-gray-500">Abhi koi report nahi bani. Upar files upload karke pehla report banao.</p>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="sm:hidden space-y-3">
              {reports.map(r => (
                <div key={r.id} className="border border-gray-100 rounded-lg p-3 space-y-2">
                  <div className="flex justify-between items-start gap-2">
                    <div>
                      <p className="font-medium text-sm text-gray-900">{r.original_filename}</p>
                      <p className="text-xs text-gray-500">{fmtDT(r.created_at)}</p>
                    </div>
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{r.stats?.mode || '?'}</span>
                  </div>
                  <div className="text-xs text-gray-500">By: {r.user_name || '-'}</div>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="text-gray-600">Total: <b>{r.stats?.total || 0}</b></span>
                    <span className="text-green-700">S: {r.stats?.success || 0}</span>
                    <span className="text-yellow-700">P: {r.stats?.pending || 0}</span>
                    <span className="text-red-700">R: {r.stats?.refunded || 0}</span>
                    {r.stats?.missing !== null && r.stats?.missing !== undefined && (
                      <span className="text-orange-700">M: {r.stats.missing}</span>
                    )}
                  </div>
                  <button type="button" onClick={() => downloadReport(r.id, r.original_filename)}
                    className="text-blue-600 hover:underline text-xs flex items-center gap-1" data-testid={`hpcl-download-${r.id}`}>
                    <DownloadSimple size={12} /> Download
                  </button>
                </div>
              ))}
            </div>

            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Created</TableHead>
                    <TableHead>By</TableHead>
                    <TableHead>Report</TableHead>
                    <TableHead>Mode</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">Success</TableHead>
                    <TableHead className="text-right">Pending</TableHead>
                    <TableHead className="text-right">Refunded</TableHead>
                    <TableHead className="text-right">Missing</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reports.map(r => (
                    <TableRow key={r.id}>
                      <TableCell className="text-xs text-gray-500 whitespace-nowrap">{fmtDT(r.created_at)}</TableCell>
                      <TableCell className="text-sm">{r.user_name}<span className="text-gray-400 text-xs ml-1">({r.user_role})</span></TableCell>
                      <TableCell className="text-sm">{r.original_filename}</TableCell>
                      <TableCell><span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{r.stats?.mode || '?'}</span></TableCell>
                      <TableCell className="text-right">{r.stats?.total || 0}</TableCell>
                      <TableCell className="text-right text-green-700">{r.stats?.success || 0}</TableCell>
                      <TableCell className="text-right text-yellow-700">{r.stats?.pending || 0}</TableCell>
                      <TableCell className="text-right text-red-700">{r.stats?.refunded || 0}</TableCell>
                      <TableCell className="text-right text-orange-700">{r.stats?.missing ?? '-'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Link to={`/hpcl-matcher/review/${r.id}`} className="text-violet-600 hover:underline text-sm flex items-center gap-1" data-testid={`hpcl-review-row-${r.id}`}>
                            <ClipboardText size={14} /> Review
                          </Link>
                          <button type="button" onClick={() => downloadReport(r.id, r.original_filename)}
                            className="text-blue-600 hover:underline text-sm flex items-center gap-1" data-testid={`hpcl-download-row-${r.id}`}>
                            <DownloadSimple size={14} /> Download
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const FileSlot = ({ label, required, file, onChange, hint, testId }) => (
  <div>
    <Label className="text-sm font-medium">
      {label} {required && <span className="text-red-500">*</span>}
    </Label>
    <div className="mt-1 relative">
      <input
        type="file"
        accept=".xlsx,.xls"
        onChange={(e) => onChange(e.target.files[0] || null)}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        data-testid={testId}
      />
      <div className={`flex items-center gap-2 border-2 border-dashed rounded-lg p-3 text-sm ${file ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-gray-50 hover:border-gray-300'}`}>
        <FileXls size={20} className={file ? 'text-blue-600' : 'text-gray-400'} />
        <div className="flex-1 overflow-hidden">
          <p className={`truncate text-sm ${file ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
            {file ? file.name : 'Select .xlsx / .xls'}
          </p>
          <p className="text-[10px] text-gray-400">{hint}</p>
        </div>
      </div>
    </div>
  </div>
);

const StatCard = ({ label, value, color }) => {
  const colors = {
    gray: 'bg-gray-50 border-gray-200 text-gray-700',
    green: 'bg-green-50 border-green-200 text-green-700',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    orange: 'bg-orange-50 border-orange-200 text-orange-700',
  };
  const Icon = color === 'green' ? CheckCircle : color === 'yellow' ? Clock : color === 'red' ? TrendDown : color === 'orange' ? Warning : TrendUp;
  return (
    <div className={`border rounded-lg p-3 ${colors[color]}`} data-testid={`stat-${color}`}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={14} />
        <span className="text-xs font-medium">{label}</span>
      </div>
      <p className="text-xl font-bold">{value}</p>
    </div>
  );
};
